/* fourpaw-standard: lifecycle-delegated */
(() => {
  if (window.SafeWalkV6RealGiftMigration) return;

  const GROUP = '_safewalk_package_group';
  const ROLE = '_safewalk_package_role';
  const REAL = '_safewalk_real_gift';
  const COMPANION = '_safewalk_companion_gift';
  const KACHING = '__kaching_bundles';
  const KINDS = ['harness_only', 'harness_bungee', 'full_set'];
  const STRICT_MARKERS = [
    '_safewalk_real_qualifying_product',
    '_safewalk_real_tag_entry',
    '_safewalk_real_dispenser_entry',
    '_safewalk_real_roll_entry'
  ];
  const propertiesFor = (item) => item?.properties && typeof item.properties === 'object'
    ? item.properties
    : {};
  const metadataFor = (item) => {
    const raw = propertiesFor(item)[KACHING];
    if (!raw) return null;
    if (typeof raw === 'object') return raw;
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch (error) {
      return null;
    }
  };
  const productId = (item) => Number(item?.product_id || 0);
  const variantId = (item) => Number(item?.variant_id || item?.id || 0);
  const lineTotal = (item) => Number.isFinite(Number(item?.final_line_price))
    ? Number(item.final_line_price)
    : Number(item?.final_price || 0) * Number(item?.quantity || 1);
  const originalTotal = (item) => Number.isFinite(Number(item?.original_line_price))
    ? Number(item.original_line_price)
    : Number(item?.original_price || 0) * Number(item?.quantity || 1);
  const identityFor = (config, role) => (config.identities || [])
    .find((identity) => identity.role === role);
  const exactIdentity = (item, identity) => Boolean(identity)
    && productId(item) === Number(identity.productId)
    && variantId(item) === Number(identity.variantId);
  const isTrue = (value) => String(value || '').toLowerCase() === 'true';
  const expectedBarFor = (metadata, kind) => (
    kind === 'harness_only' ? metadata?.bid : metadata?.bar
  );
  const isLegacyMain = (item, config) => {
    const properties = propertiesFor(item);
    const metadata = metadataFor(item);
    const kind = properties._safewalk_package;
    const groupId = String(properties[GROUP] || '');
    const privateId = String(metadata?.id || '');
    const legacyGiftId = String(config.legacyGiftIds?.[kind] || '');
    return properties[ROLE] === 'main'
      && Boolean(groupId)
      && KINDS.includes(kind)
      && productId(item) === Number(config.qualifyingProductId)
      && Number(item.quantity) === 1
      && lineTotal(item) > 0
      && STRICT_MARKERS.every((key) => !properties[key])
      && metadata?.main === true
      && metadata?.deal === config.deal
      && expectedBarFor(metadata, kind) === config.bars?.[kind]
      && Boolean(privateId)
      && Boolean(legacyGiftId);
  };
  const staleGroups = (cart, config = {}) => {
    const items = Array.isArray(cart?.items) ? cart.items : [];
    const dispenserIdentity = identityFor(config, 'gift_dispenser');
    if (!dispenserIdentity || !Number(config.legacyCompanionProductId)) return [];
    return items.filter((item) => isLegacyMain(item, config)).flatMap((main) => {
      const properties = propertiesFor(main);
      const metadata = metadataFor(main);
      const groupId = String(properties[GROUP]);
      const privateId = String(metadata.id);
      const kind = properties._safewalk_package;
      const sameGroupMains = items.filter((item) => {
        const candidate = propertiesFor(item);
        return candidate[ROLE] === 'main' && String(candidate[GROUP] || '') === groupId;
      });
      const samePrivateMains = items.filter((item) => (
        metadataFor(item)?.main === true
        && String(metadataFor(item)?.id || '') === privateId
        && metadataFor(item)?.deal === config.deal
      ));
      const markedCurrentGifts = items.filter((item) => {
        const candidate = propertiesFor(item);
        return isTrue(candidate[REAL]) && String(candidate[GROUP] || '') === groupId;
      });
      const legacyDispenser = items.filter((item) => {
        const candidate = propertiesFor(item);
        const candidateMetadata = metadataFor(item);
        return !isTrue(candidate[REAL])
          && exactIdentity(item, dispenserIdentity)
          && Number(item.quantity) === 1
          && lineTotal(item) === 0
          && originalTotal(item) > 0
          && candidateMetadata?.deal === config.deal
          && String(candidateMetadata?.id || '') === privateId
          && candidateMetadata?.gift === config.legacyGiftIds[kind];
      });
      const companions = items.filter((item) => {
        const candidate = propertiesFor(item);
        return isTrue(candidate[COMPANION])
          && candidate[ROLE] === 'companion_gift'
          && String(candidate[GROUP] || '') === groupId
          && productId(item) === Number(config.legacyCompanionProductId);
      });
      if (sameGroupMains.length !== 1
        || samePrivateMains.length !== 1
        || markedCurrentGifts.length
        || legacyDispenser.length > 1
        || companions.length > 1) return [];
      const linked = items.filter((item) => {
        const candidateMetadata = metadataFor(item);
        return candidateMetadata?.deal === config.deal
          && String(candidateMetadata?.id || '') === privateId;
      });
      const removalItems = [...new Set([...linked, ...companions])];
      if (!removalItems.includes(main) || removalItems.some((item) => !item.key)) return [];
      return [{
        groupId,
        privateId,
        kind,
        variantId: variantId(main),
        removalKeys: removalItems.map((item) => item.key)
      }];
    });
  };
  const removalUpdates = (groups = []) => Object.fromEntries(
    groups.flatMap((group) => group.removalKeys || []).map((key) => [key, 0])
  );
  const readdUrl = (groups = [], config = {}) => {
    if (!config.readdUrl) return '';
    const url = new URL(config.readdUrl, window.location.origin);
    const firstVariant = Number(groups[0]?.variantId || 0);
    if (firstVariant > 0) url.searchParams.set('variant', String(firstVariant));
    url.searchParams.set('safewalk_readd', '1');
    return url.href;
  };

  window.SafeWalkV6RealGiftMigration = Object.freeze({
    staleGroups,
    removalUpdates,
    readdUrl
  });
})();
